import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useConnectionInfo } from '../../hooks/useCaapApi';
import { useTheme } from '../../context/ThemeContext';

interface PageLayoutProps {
  children: React.ReactNode;
  navItems?: { path: string; label: string }[];
}

const defaultNavItems = [
  { path: '/caap', label: 'Dashboard' },
  { path: '/caap/runs', label: 'Run History' },
  { path: '/caap/parameters', label: 'Parameters' },
];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const connected = status === 'Connected';
  return (
    <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${
        connected ? 'bg-green-500' : 'bg-red-500'
      }`} />
      {status}
    </span>
  );
};

export const PageLayout: React.FC<PageLayoutProps> = ({ children, navItems = defaultNavItems }) => {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: connInfo, isLoading: connLoading } = useConnectionInfo();
  const { resolved, toggle } = useTheme();

  // Longest match wins, so exactly one tab is active: a root item ("/caap", "/deficits")
  // matches its own children, but a more specific sibling ("/caap/runs") outranks it.
  const matchesPath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const activePath = navItems.reduce<string | null>(
    (best, item) =>
      matchesPath(item.path) && (best === null || item.path.length > best.length)
        ? item.path
        : best,
    null
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    }
    if (settingsOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [settingsOpen]);

  return (
    <div className="min-h-screen bg-surface">
      <nav className="bg-surface-alt border-b border-border-default">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-lg font-semibold text-primary">
                Corp Admin
              </Link>
              <div className="flex space-x-1">
                {navItems.map((item) => {
                  const isActive = item.path === activePath;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`rounded-md px-3 py-2 text-sm font-medium ${
                        isActive
                          ? 'bg-surface-hover text-primary'
                          : 'text-muted hover:bg-surface-hover hover:text-secondary'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center space-x-1">
              {/* Theme Toggle */}
              <button
                onClick={toggle}
                className="rounded-md p-2 text-muted hover:bg-surface-hover hover:text-secondary"
                title={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {resolved === 'dark' ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                  </svg>
                )}
              </button>

              {/* Settings Gear */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="rounded-md p-2 text-muted hover:bg-surface-hover hover:text-secondary"
                  title="Connection Info"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                </button>

                {settingsOpen && (
                  <div className="absolute right-0 mt-2 w-80 rounded-lg border border-border-default bg-surface-alt shadow-lg z-50">
                    <div className="px-4 py-3 border-b border-border-default">
                      <h3 className="text-sm font-semibold text-primary">Connection Info</h3>
                    </div>
                    <div className="px-4 py-3 space-y-3">
                      {connLoading ? (
                        <p className="text-sm text-muted">Loading...</p>
                      ) : connInfo ? (
                        <>
                          <div>
                            <p className="text-xs font-medium uppercase text-muted">Server</p>
                            <p className="text-sm font-mono text-primary">{connInfo.server}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-muted">Corp Database</p>
                            <p className="text-sm font-mono text-primary">{connInfo.database}</p>
                            <StatusBadge status={connInfo.corpStatus} />
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-muted">JCSA Database</p>
                            <p className="text-sm font-mono text-primary">{connInfo.jcsaDatabase}</p>
                            <StatusBadge status={connInfo.jcsaStatus} />
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-muted">Unable to fetch connection info</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
};
